import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Package } from 'lucide-react';

const PREVIEW_MAX_W = 360;
const PREVIEW_MAX_H = 480;
const LEAVE_DELAY_MS = 220;

function clampPreviewPosition(rect: DOMRect): { left: number; top: number } {
  const gap = 10;
  let left = rect.right + gap;
  const topMin = 8;
  const topMax = Math.max(topMin, window.innerHeight - PREVIEW_MAX_H - 8);
  let top = Math.min(Math.max(rect.top, topMin), topMax);

  if (left + PREVIEW_MAX_W > window.innerWidth - 8) {
    left = rect.left - gap - PREVIEW_MAX_W;
  }
  left = Math.max(8, Math.min(left, window.innerWidth - PREVIEW_MAX_W - 8));

  return { left, top };
}

export interface ProductThumbProps {
  imageUrl: string | null | undefined;
  /**
   * When true, hover shows a large fixed-position preview (escapes scroll/overflow parents).
   * @default true
   */
  popOut?: boolean;
}

/** Thumbnail with optional floating hover preview for reading label art. */
export function ProductThumb({ imageUrl, popOut = true }: ProductThumbProps) {
  const [failed, setFailed] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [previewPos, setPreviewPos] = useState<{ left: number; top: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const leaveTimeoutRef = useRef<number | null>(null);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimeoutRef.current != null) {
      window.clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const hidePreview = useCallback(() => {
    setHoverOpen(false);
    setPreviewPos(null);
  }, []);

  const scheduleHidePreview = useCallback(() => {
    clearLeaveTimer();
    leaveTimeoutRef.current = window.setTimeout(hidePreview, LEAVE_DELAY_MS);
  }, [clearLeaveTimer, hidePreview]);

  useEffect(() => () => clearLeaveTimer(), [clearLeaveTimer]);

  useEffect(() => {
    if (!hoverOpen) return;
    const onRepositionOrClose = () => {
      hidePreview();
    };
    window.addEventListener('scroll', onRepositionOrClose, true);
    window.addEventListener('resize', onRepositionOrClose);
    return () => {
      window.removeEventListener('scroll', onRepositionOrClose, true);
      window.removeEventListener('resize', onRepositionOrClose);
    };
  }, [hoverOpen, hidePreview]);

  const openPreview = useCallback(() => {
    clearLeaveTimer();
    if (!popOut) return;
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPreviewPos(clampPreviewPosition(rect));
    setHoverOpen(true);
  }, [clearLeaveTimer, popOut]);

  const src = typeof imageUrl === 'string' ? imageUrl.trim() : '';

  if (!src || failed) {
    return (
      <div
        ref={anchorRef}
        className="flex h-full w-full items-center justify-center text-muted-foreground"
      >
        <Package className="h-6 w-6" aria-hidden />
      </div>
    );
  }

  const previewPortal =
    popOut &&
    hoverOpen &&
    previewPos &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="pointer-events-auto fixed z-[100] rounded-lg border bg-background p-2 shadow-2xl"
        style={{
          left: previewPos.left,
          top: previewPos.top,
          maxWidth: PREVIEW_MAX_W,
          maxHeight: PREVIEW_MAX_H + 16,
        }}
        onMouseEnter={clearLeaveTimer}
        onMouseLeave={scheduleHidePreview}
      >
        <img
          src={src}
          alt=""
          className="max-h-[min(480px,70vh)] w-full max-w-[min(360px,calc(100vw-48px))] object-contain"
          loading="eager"
          decoding="async"
        />
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={anchorRef}
        className="relative h-full w-full"
        onMouseEnter={openPreview}
        onMouseLeave={scheduleHidePreview}
      >
        <img
          src={src}
          alt=""
          className="h-full w-full object-contain object-center"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      </div>
      {previewPortal}
    </>
  );
}
